import { trpc } from '$src/lib/trpc'
import { customerHooks, propertyHooks } from '$src/lib/trpcHooks'
import { ServicePromotionCard } from '$src/modules/subscription/components'
import { Badge, Card, Center, Group, Loader, Stack, Text } from '@mantine/core'
import { useDocumentVisibility } from '@mantine/hooks'
import { AppPage, createChatHooks } from '@markku/components'
import { partition } from '@markku/utils'

import { IconMessages } from '@tabler/icons-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

function MessagesListImplementation() {
  const navigate = useNavigate()
  const documentVisibility = useDocumentVisibility()

  const chatHooks = createChatHooks(trpc)
  const { data: rooms, isLoading } = chatHooks.useGetRooms({
    enabled: documentVisibility === 'visible',
    staleTime: 5000,
    refetchInterval: documentVisibility === 'visible' ? 15000 : false,
  })
  const { data: customersData } = customerHooks.useList()
  const customers = customersData?.data || []
  const firstCustomer = customers[0]

  const { mutateAsync: findOrCreateRoom, isPending } =
    chatHooks.useFindOrCreateCustomerServiceRoom()

  const handleCustomerServiceClick = async (customerId: number) => {
    if (isPending) return
    const room = await findOrCreateRoom({ customerId })
    navigate({ to: `/messages/${room.id}` })
  }

  // Find customer service room if it exists in the list
  const [customerServiceRooms, otherRooms] = partition(
    rooms || [],
    ({ room }) => room.type === 'customer_service',
  )

  const getCustomerServiceRoom = () => ({
    room: {
      id: 0,
      name: 'Asiakaspalvelu',
      type: 'customer_service' as const,
      customerId: firstCustomer?.id || 0,
    },
    lastMessage: null,
    unreadCount: 0,
  })

  if (isLoading || !rooms) {
    return (
      <AppPage title="Viestit" backTarget="/home">
        <Center h={400}>
          <Loader />
        </Center>
      </AppPage>
    )
  }

  const allRooms = [
    ...(customerServiceRooms.length
      ? customerServiceRooms
      : [getCustomerServiceRoom()]),
    ...otherRooms,
  ]

  return (
    <AppPage title="Viestit" backTarget="/home">
      <Stack>
        {allRooms.length === 0 ? (
          <Card p="xl" withBorder>
            <Stack align="center">
              <IconMessages size={48} color="gray" />
              <Text c="dimmed">Ei keskusteluja vielä</Text>
            </Stack>
          </Card>
        ) : (
          allRooms.map(({ room, lastMessage, unreadCount }) => {
            const isCustomerService = room.type === 'customer_service'

            return (
              <Card
                key={room.id}
                p="md"
                withBorder
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  isCustomerService
                    ? handleCustomerServiceClick(room.customerId as number)
                    : navigate({ to: `/messages/${room.id}` })
                }
              >
                <Group justify="space-between">
                  <Group>
                    <Group>
                      <Text fw={600}>{room.name}</Text>
                      {unreadCount > 0 && (
                        <Badge color="red" variant="filled">
                          {unreadCount}
                        </Badge>
                      )}
                    </Group>
                    {lastMessage ? (
                      <Text size="sm" c="dimmed" lineClamp={1}>
                        {lastMessage.content}
                      </Text>
                    ) : (
                      <Text size="sm" c="dimmed" fs="italic">
                        Ei viestejä vielä - aloita keskustelu
                      </Text>
                    )}
                  </Group>

                  {lastMessage && (
                    <Text size="xs" c="dimmed">
                      {dayjs(lastMessage.createdAt).fromNow()}
                    </Text>
                  )}
                </Group>
              </Card>
            )
          })
        )}
      </Stack>
    </AppPage>
  )
}

function MessagesListPage() {
  // Check if user has any active subscription
  const { data: propertiesResponse } = propertyHooks.useList()
  const isPremium =
    propertiesResponse?.data.some((property) =>
      property.subscriptions?.some((sub) => sub.status === 'active'),
    ) ?? false

  if (!isPremium) {
    return (
      <AppPage title="Viestit" backTarget="/home">
        <ServicePromotionCard
          title="Viestit Markun kanssa"
          description="Markku.fi palvelun tilaajat voivat viestiä suoraan huoltohenkilön kanssa sopiakseen käyntiaikoja, esittääkseen kysymyksiä ja pyytääkseen apua esimerkiksi pieniin korjaus- tai asennustarpeisiin."
          benefits={[
            'Suora viestintä huoltohenkilön kanssa',
            'Sopikaa käyntiajat helposti',
            'Kysy, pyydä apua ja seuraa käyntejä',
          ]}
          showAvailabilityNote
        />
      </AppPage>
    )
  }

  return <MessagesListImplementation />
}

export const Route = createFileRoute('/_app/messages/')({
  component: MessagesListPage,
})
